import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { RoleBadge } from '../common/RoleBadge';
import * as adminService from '../../services/adminService';

interface Invite {
  id: string;
  email: string;
  role_name: string;
  status: string;
  token: string;
  created_at: string;
  invite_url?: string;
}

interface InvitesListProps {
  refreshTrigger?: number;
}

const statusColors: Record<string, { light: string; dark: string }> = {
  pending: {
    light: 'bg-green-100 text-green-800 border border-green-300',
    dark: 'bg-green-900 text-green-200 border border-green-700',
  },
  accepted: {
    light: 'bg-gray-100 text-gray-800 border border-gray-300',
    dark: 'bg-gray-700 text-gray-200 border border-gray-600',
  },
  revoked: {
    light: 'bg-red-100 text-red-800 border border-red-300',
    dark: 'bg-red-900 text-red-200 border border-red-700',
  },
  expired: {
    light: 'bg-red-100 text-red-800 border border-red-300',
    dark: 'bg-red-900 text-red-200 border border-red-700',
  },
};

const defaultStatusColors = {
  light: 'bg-gray-100 text-gray-800 border border-gray-300',
  dark: 'bg-gray-700 text-gray-200 border border-gray-600',
};

export const InvitesList: React.FC<InvitesListProps> = ({ refreshTrigger }) => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();

  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getInvites();
      setInvites(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites, refreshTrigger]);

  const handleRevoke = async (inviteId: string) => {
    try {
      await adminService.revokeInvite(inviteId);
      showToast('Invite revoked', 'success');
      fetchInvites();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to revoke invite', 'error');
    }
  };

  const handleCopyLink = async (invite: Invite) => {
    const url = invite.invite_url || `${window.location.origin}/invite/${invite.token}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Invite link copied', 'success');
    } catch {
      showToast('Failed to copy link', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 p-4 rounded-md bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-100">
        {error}
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <div className="text-center py-12">
        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
          No invites yet. Create one to get started.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Email
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Role
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Created
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
            {invites.map((invite) => {
              const sc = statusColors[invite.status] || defaultStatusColors;
              return (
                <tr key={invite.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {invite.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <RoleBadge role={invite.role_name} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      darkMode ? sc.dark : sc.light
                    }`}>
                      {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(invite.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {invite.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleCopyLink(invite)}
                          className="px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white mr-3"
                        >
                          Copy Link
                        </button>
                        <button
                          onClick={() => handleRevoke(invite.id)}
                          className="px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white"
                        >
                          Revoke
                        </button>
                      </>
                    )}
                    {invite.status !== 'pending' && (
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>--</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvitesList;
